/** Explicit allowlist: never forward arbitrary query parameters or profile data. */
export const CAMPAIGN_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "campaign_id",
  "adset_id",
  "ad_id",
  "publisher_platform",
  "placement",
  "fbclid",
  "ScCid",
  "twclid",
] as const;
export const ACQUISITION_COOKIE = "hiive_acquisition";
export const ACQUISITION_MAX_AGE = 90 * 24 * 60 * 60;
/**
 * Ad measurement is ON by default (opt-out). "unknown" means the person never
 * touched the setting and is measured; only an explicit "denied" from the Ad
 * Measurement settings page turns it off. iOS ATT remains a separate, system
 * gate for per-user tracking (IDFA, account and contact matching).
 */
export type AdConsent = "unknown" | "granted" | "denied";
export function measurementOn(consent: AdConsent | undefined): boolean {
  return consent !== "denied";
}
export const AD_MATCHING_NOTICE_VERSION = 2;
export type Touch = {
  id: string;
  at: number;
  source: "url";
  status: "observed" | "organic" | "attributed" | "restricted" | "unavailable";
  campaign: Record<string, string>;
};
export type MetaAppSignals = {
  anon_id: string;
  madid?: string;
  /** Meta's 16-slot extinfo array ("i2"/"a2", package, versions, OS, ...). */
  extinfo: string[];
};
export type MetaClientNetwork = {
  user_agent?: string;
  ip?: string;
  /** Origin + path of the page that synced, never its query string. */
  page_url?: string;
  at: number;
};
/** Website request details are reused for at most Meta's 7-day event window. */
export const META_CLIENT_MAX_AGE_MS = 7 * 86400_000;
export type Acquisition = {
  version: 1;
  first?: Touch;
  last?: Touch;
  owner_user_id?: string;
  installation_id?: string;
  /** Native SDK is present and measurement is currently consented. */
  meta_native_measurement?: boolean;
  meta_sdk_available?: boolean;
  meta_fbp?: string;
  /** Pixel's `_fbc` click cookie (web), preferred over a rebuilt fbclid. */
  meta_fbc?: string;
  /**
   * Native app signals for server-side app events (Conversions API with
   * action_source "app"), so purchases reach Meta even if the app never
   * reopens: the SDK's install ID, the device's extinfo array and, only when
   * tracking is allowed (ATT on iOS), the advertising ID.
   */
  meta_app?: MetaAppSignals;
  native_os_version?: string;
  native_app_version?: string;
  app_surface?: "web" | "pwa" | "ios_native" | "android_native";
  consent: AdConsent;
  consent_at?: number;
  consent_notice_version?: number;
  /**
   * Server-only. The consenting browser's request details, so a purchase that
   * a gateway webhook confirms (no browser on that request) still carries the
   * user agent and IP Meta requires for website events. Never sent to the
   * client, and dropped the moment measurement is turned off.
   */
  meta_client?: MetaClientNetwork;
  /** ATT permits individual cross-app tracking; aggregate measurement is separate. */
  att_status?:
    | "authorized"
    | "denied"
    | "restricted"
    | "notDetermined"
    | "unavailable";
};
function hasControlCharacters(value: string) {
  return Array.from(value).some((character) => character.charCodeAt(0) < 32);
}
export function campaignParameters(url: URL): Record<string, string> {
  const values: Record<string, string> = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = url.searchParams.get(key)?.trim();
    // Ad URL templates can arrive without Meta expanding their macros.
    // Keep those out of attribution dimensions rather than treating them as IDs.
    if (
      value &&
      value.length <= 256 &&
      !hasControlCharacters(value) &&
      !/^\{\{[^{}]+\}\}$/.test(value)
    )
      values[key] = value;
  }
  return values;
}
export function preserveCampaignParameters(
  target: string,
  incoming: string,
): string {
  const destination = new URL(target, "https://app.hiive.club");
  for (const [key, value] of Object.entries(
    campaignParameters(new URL(incoming, "https://app.hiive.club")),
  )) {
    // The current click wins over stale campaign tags embedded in a shared link.
    destination.searchParams.set(key, value);
  }
  return target.startsWith("/")
    ? `${destination.pathname}${destination.search}${destination.hash}`
    : destination.toString();
}
function sanitizeTouch(value: unknown, now: number): Touch | undefined {
  if (!value || typeof value !== "object") return;
  const t = value as Touch;
  if (
    typeof t.id !== "string" ||
    !/^[\w-]{1,100}$/.test(t.id) ||
    typeof t.at !== "number" ||
    !Number.isFinite(t.at) ||
    t.at > now + 60_000 ||
    t.at < now - ACQUISITION_MAX_AGE * 1000
  )
    return;
  if (
    !["url"].includes(t.source) ||
    ![
      "observed",
      "organic",
      "attributed",
      "restricted",
      "unavailable",
    ].includes(t.status)
  )
    return;
  const campaign: Record<string, string> = {};
  let campaignBytes = 0;
  for (const key of [
    ...CAMPAIGN_KEYS,
    "media_source",
    "channel",
    "match_type",
  ]) {
    const v = t.campaign?.[key];
    if (
      typeof v === "string" &&
      v.length <= 256 &&
      !hasControlCharacters(v) &&
      campaignBytes + new TextEncoder().encode(v).length <= 1000
    ) {
      campaign[key] = v;
      campaignBytes += new TextEncoder().encode(v).length;
    }
  }
  return { id: t.id, at: t.at, source: t.source, status: t.status, campaign };
}
function sanitizeMetaApp(value: unknown): MetaAppSignals | undefined {
  if (!value || typeof value !== "object") return;
  const m = value as MetaAppSignals;
  if (typeof m.anon_id !== "string" || !/^[\w.:-]{1,100}$/.test(m.anon_id))
    return;
  if (
    !Array.isArray(m.extinfo) ||
    m.extinfo.length !== 16 ||
    !["i2", "a2"].includes(m.extinfo[0] ?? "") ||
    m.extinfo.some(
      (v) => typeof v !== "string" || v.length > 100 || hasControlCharacters(v),
    )
  )
    return;
  const result: MetaAppSignals = {
    anon_id: m.anon_id,
    extinfo: [...m.extinfo],
  };
  // An all-zero IDFA/GAID is what the OS returns when tracking is limited.
  if (
    typeof m.madid === "string" &&
    /^[0-9A-Fa-f-]{36}$/.test(m.madid) &&
    !/^[0-]+$/.test(m.madid)
  )
    result.madid = m.madid;
  return result;
}
function sanitizeMetaClient(
  value: unknown,
  now: number,
): MetaClientNetwork | undefined {
  if (!value || typeof value !== "object") return;
  const c = value as MetaClientNetwork;
  if (
    typeof c.at !== "number" ||
    !Number.isFinite(c.at) ||
    c.at > now + 60_000 ||
    c.at < now - ACQUISITION_MAX_AGE * 1000
  )
    return;
  const result: MetaClientNetwork = { at: c.at };
  if (
    typeof c.user_agent === "string" &&
    c.user_agent.length <= 512 &&
    !hasControlCharacters(c.user_agent)
  )
    result.user_agent = c.user_agent;
  if (typeof c.ip === "string" && /^[0-9a-fA-F.:]{3,45}$/.test(c.ip))
    result.ip = c.ip;
  if (typeof c.page_url === "string" && c.page_url.length <= 512) {
    try {
      const url = new URL(c.page_url);
      if (url.protocol === "https:" || url.protocol === "http:")
        result.page_url = `${url.origin}${url.pathname}`;
    } catch {
      /* Not a URL. */
    }
  }
  return result.user_agent || result.ip || result.page_url ? result : undefined;
}
/** Removes server-only request details before a context reaches a browser. */
export function publicAcquisition(context: Acquisition): Acquisition {
  const { meta_client: _serverOnly, ...rest } = context;
  return rest;
}
export function sanitizeAcquisition(
  value: unknown,
  now = Date.now(),
): Acquisition {
  const a = (value && typeof value === "object" ? value : {}) as Acquisition;
  const result: Acquisition = {
    version: 1,
    consent: ["granted", "denied"].includes(a.consent) ? a.consent : "unknown",
  };
  if (a.consent_notice_version === AD_MATCHING_NOTICE_VERSION)
    result.consent_notice_version = AD_MATCHING_NOTICE_VERSION;
  result.first = sanitizeTouch(a.first, now);
  result.last = sanitizeTouch(a.last, now);
  for (const key of ["installation_id", "owner_user_id"] as const) {
    if (typeof a[key] === "string" && /^[\w-]{1,100}$/.test(a[key]))
      result[key] = a[key];
  }
  for (const key of ["meta_native_measurement", "meta_sdk_available"] as const)
    if (typeof a[key] === "boolean") result[key] = a[key];
  for (const key of ["native_os_version", "native_app_version"] as const) {
    if (typeof a[key] === "string" && /^[\w.+-]{1,50}$/.test(a[key]))
      result[key] = a[key];
  }
  if (
    typeof a.meta_fbp === "string" &&
    /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_-]{1,100}$/.test(a.meta_fbp)
  )
    result.meta_fbp = a.meta_fbp;
  if (
    ["web", "pwa", "ios_native", "android_native"].includes(a.app_surface ?? "")
  )
    result.app_surface = a.app_surface;
  if (
    [
      "authorized",
      "denied",
      "restricted",
      "notDetermined",
      "unavailable",
    ].includes(a.att_status ?? "")
  )
    result.att_status = a.att_status;
  if (
    typeof a.meta_fbc === "string" &&
    /^fb\.\d\.\d{10,16}\.[\w-]{1,500}$/.test(a.meta_fbc)
  )
    result.meta_fbc = a.meta_fbc;
  const app = sanitizeMetaApp(a.meta_app);
  if (app) result.meta_app = app;
  const client = sanitizeMetaClient(a.meta_client, now);
  if (client) result.meta_client = client;
  if (
    typeof a.consent_at === "number" &&
    Number.isFinite(a.consent_at) &&
    a.consent_at <= now + 60_000 &&
    a.consent_at > now - ACQUISITION_MAX_AGE * 1000
  )
    result.consent_at = a.consent_at;
  return result;
}
export function mergeAcquisition(
  previous: Acquisition,
  incoming: Acquisition,
): Acquisition {
  const first =
    !previous.first || (incoming.first && incoming.first.at < previous.first.at)
      ? incoming.first
      : previous.first;
  const last =
    !previous.last || (incoming.last && incoming.last.at >= previous.last.at)
      ? incoming.last
      : previous.last;
  // Withdrawal wins a timestamp tie; stale tabs cannot re-enable measurement.
  const consentWins =
    (incoming.consent_at ?? 0) > (previous.consent_at ?? 0) ||
    (incoming.consent_at === previous.consent_at &&
      incoming.consent === "denied");
  const merged: Acquisition = {
    ...previous,
    ...Object.fromEntries(
      Object.entries(incoming).filter(([, value]) => value !== undefined),
    ),
    first,
    last,
    consent: consentWins ? incoming.consent : previous.consent,
    consent_at: consentWins ? incoming.consent_at : previous.consent_at,
    consent_notice_version: consentWins
      ? incoming.consent_notice_version
      : previous.consent_notice_version,
  };
  // Request details exist only while measurement is on; opting out erases them.
  if (!measurementOn(merged.consent)) delete merged.meta_client;
  return merged;
}
export function acquisitionProperties(a: Acquisition): Record<string, unknown> {
  const props: Record<string, unknown> = {
    acquisition_schema_version: 1,
    ad_measurement_consent: a.consent,
    ad_measurement_notice_version: a.consent_notice_version,
    installation_id: a.installation_id,
    native_app_version: a.native_app_version,
    attribution_provider: a.last?.source,
    attribution_status: a.last?.status,
    att_status: a.att_status,
  };
  for (const [prefix, touch] of [
    ["first", a.first],
    ["last", a.last],
  ] as const) {
    if (!touch) continue;
    props[`${prefix}_touch_at`] = new Date(touch.at).toISOString();
    for (const [key, value] of Object.entries(touch.campaign)) {
      // Click IDs are needed by conversion APIs, not as analytics dimensions.
      if (!["fbclid", "ScCid", "twclid"].includes(key))
        props[`${prefix}_${key}`] = value;
    }
  }
  return props;
}
export function advertisingAllowed(a: Acquisition): boolean {
  return (
    measurementOn(a.consent) &&
    (a.app_surface !== "ios_native" || a.att_status === "authorized")
  );
}
/** Hashed verified email/phone follow the same default-on rule (and ATT on iOS). */
export function contactMatchingAllowed(a: Acquisition): boolean {
  return advertisingAllowed(a);
}
export function safeDeepLinkPath(value: unknown): string | null {
  if (
    typeof value !== "string" ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    hasControlCharacters(value)
  )
    return null;
  const url = new URL(value, "https://app.hiive.club");
  if (
    !/^\/(hiives|shop|profile|sessions|post|activity-post|movement|weight-training|invite|r|c|l)(\/|$)/.test(
      url.pathname,
    )
  )
    return null;
  return `${url.pathname}${url.search}${url.hash}`;
}
